//Bibliotecas
import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
//Componentes material
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import CssBaseline from '@material-ui/core/CssBaseline';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Autocomplete from '@material-ui/lab/Autocomplete';
//Componentes
import Layout from './Layout';
import VitalizePaper from '../components/VitalizePaper';
import LabelSecao from '../components/LabelSecao';
import MaskedInput from 'react-text-mask';
import VitalizeFab from '../components/VitalizeFab';
//Controllers
import ClienteController from '../controllers/ClienteController';
import GeneroController from '../controllers/GeneroController';
import EmpresaController from '../controllers/EmpresaController';
import PermissaoController from '../controllers/PermissaoController';
import PatologiaController from '../controllers/PatologiaController';
import CepController from '../controllers/CepController';
//Utils
import deepCopy from '../utils/deepCopy';

/**
 * Página com um formulário para edição e inclusão de clientes
 */
class ClienteFormPage extends React.Component {

    constructor(props) {
        super(props);
        //Bindings
        this.handleChange = this.handleChange.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleEmpresaChange = this.handleEmpresaChange.bind(this);
        this.getEmpresaLabel = this.getEmpresaLabel.bind(this);
        this.getEmpresaSelected = this.getEmpresaSelected.bind(this);
        this.handlePatologiaChange = this.handlePatologiaChange.bind(this);
        this.getPatologiaLabel = this.getPatologiaLabel.bind(this);
        this.getPatologiaSelected = this.getPatologiaSelected.bind(this);
        this.handleCEPChange = this.handleCEPChange.bind(this);
        this.handleUFChange = this.handleUFChange.bind(this);
        //Controller
        this.clienteController = new ClienteController();
        this.generoController = new GeneroController();
        this.empresaController = new EmpresaController();
        this.permissaoController = new PermissaoController();
        this.patologiaController = new PatologiaController();
        this.cepController = new CepController();
        //Estado inicial
        this.state = {
            valores: {
                nome: '',
                cpf: '',
                genero: '',
                dataNascimento: new Date().toISOString().substring(0, 10),
                patologia: null,
                ativo: true,
                profissao: '',
                email: '',
                telefone: '',
                senha: '',
                empresa: null,
                cep: '',
                logradouro: '',
                numero: '',
                complemento: '',
                bairro: '',
                municipio: '',
                uf: ''
            },
            erros: {
                nome: '',
                cpf: '',
                genero: '',
                dataNascimento: '',
                patologia: '',
                ativo: '',
                profissao: '',
                email: '',
                telefone: '',
                senha: '',
                cep: '',
                empresa: '',
                logradouro: '',
                numero: '',
                complemento: '',
                bairro: '',
                municipio: '',
                uf: ''
            },
            generos: [],
            patologias: [],
            empresas: [],
            sucesso: false,
            processando: false,
            carregando: true,
            permiteSelecionarEmpresa: false,
            permiteCadastrarCliente: false
        };
    }

    /**
     * Carrega os dados do cliente sendo editado e dos gêneros
     */
    async componentDidMount() {
        const id = this.getId();
        const state = Object.assign({}, this.state);
        state.carregando = false; //Indica que a consulta terminou

        //Consultando permissão
        state.permiteCadastrarCliente = await this.permissaoController.permiteCadastrarCliente();

        //Consulta os gêneros e as patologias
        state.generos = await this.obterGeneros();
        state.patologias = await this.obterPatologias();

        //Consulta as empresas
        state.permiteSelecionarEmpresa = await this.permissaoController.permiteSelecionarEmpresa();
        if (state.permiteSelecionarEmpresa)
            state.empresas = await this.obterEmpresas();

        if (id > 0) {
            //Carrega o cliente
            const cliente = await this.clienteController.obter(id);
            if (cliente && cliente.sucesso) {
                state.valores = {
                    nome: cliente.nome,
                    cpf: cliente.cpf,
                    genero: cliente.genero,
                    patologia: state.patologias.find(patologia => patologia.id === cliente.patologia) ?? null, //Carrega a patologia para exibição no combo
                    ativo: cliente.ativo,
                    profissao: cliente.profissao,
                    empresa: state.empresas.find(empresa => empresa.id === cliente.empresa), //Carrega a empresa para exibição no combo
                    dataNascimento: cliente.dataNascimento,
                    email: cliente.email,
                    telefone: cliente.telefone ?? '',
                    senha: '',
                    cep: cliente.cep ?? '',
                    logradouro: cliente.logradouro ?? '',
                    numero: cliente.numero ?? '',
                    complemento: cliente.complemento ?? '',
                    bairro: cliente.bairro ?? '',
                    municipio: cliente.municipio ?? '',
                    uf: cliente.uf ?? ''
                };
            }
        }

        this.setState(state);
    }

    /**
     * Carrega os gêneros da API
     */
    async obterGeneros() {
        const result = await this.generoController.listar();
        if (result.sucesso)
            return result.items;
        else
            return [];
    }

    /**
     * Carrega as patologias da API
     */
    async obterPatologias() {
        const result = await this.patologiaController.listar();
        if (result.sucesso)
            return result.items;
        else
            return [];
    }

    /**
     * Carrega as empresas da API
     */
    async obterEmpresas() {
        const result = await this.empresaController.listarSelecao();
        if (result.sucesso)
            return result.items;
        else
            return [];
    }

    /**
     * Recupera o Id do cliente a ser editado dos parâmetros da URL
     */
    getId() {
        let id;
        if (this.props.match)
            id = this.props.match.params.id;
        if (id === 'add')
            return 0;
        else
            return parseInt(id);
    }

    /**
     * Trata a alteração no campo CEP, consultando os dados na API ViaCep
     * @param {*} event Dados do enveto
     */
    async handleCEPChange(event) {
        //Altera somente se o usuário possuir permissão
        if (this.state.permiteCadastrarCliente) {
            //Cria uma cópia do estado
            const state = deepCopy(this.state);
            //Obtem o valor digitado
            let cep = event.target.value;
            //Altera o CEP
            state.valores.cep = cep;
            //Limpa os caracteres não numéricos
            cep = cep.replace(/\D/g, '');

            //Verifica se o CEP está preenchido
            if (cep && cep.length === 8) {
                const dados = await this.cepController.consultarCep(cep);

                //Preenche o município e a UF
                state.valores.municipio = dados.municipio;
                state.valores.uf = dados.uf;
                //Somente substitui se o CEP possuir um endereço
                if (dados.logradouro)
                    state.valores.logradouro = dados.logradouro;
                //Somente substitui se o CEP possuir um bairro
                if (dados.bairro)
                    state.valores.bairro = dados.bairro;
            }

            //Altera o estado do componente
            this.setState(state);
        }
    }

    /**
     * Trata a alteração do campo UF
     * @param {*} evt Objeto contendo os dados do evento
     * @param {String} novaUF Nova UF selecionada pelo usuário
     */
    handleUFChange(evt, novaUF) {
        //Altera somente se o usuário possuir permissão
        if (this.state.permiteCadastrarCliente) {
            //Cria uma cópia do estado atual e atualiza o campo
            const state = deepCopy(this.state);
            state.valores.uf = novaUF;
            //Atualiza o estado da página
            this.setState(state);
        }
    }

    /**
     * Trata a alteração do campo empresa
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novaEmpresa Dados da empresa selecionada
     */
    async handleEmpresaChange(evt, novaEmpresa) {
        //Altera somente se o usuário possuir permissão
        if (this.state.permiteCadastrarCliente) {
            //Cria uma cópia do estado atual e atualiza o campo
            const state = deepCopy(this.state);
            state.valores.empresa = novaEmpresa;
            //Atualiza o estado da página
            this.setState(state);
        }
    }

    /**
     * Retorna os dados da empresa para exibição no campo
     * @param {Object} empresa Dados da empresa
     * @param {String} empresa.nome Nome da empresa
     * @returns {String} Retorna o nome da empresa
     */
    getEmpresaLabel(empresa) {
        return empresa.nome;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da empresa
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da empresa selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getEmpresaSelected(opcao, valor) {
        //Compara a identificação das duas empresas
        return opcao.id === valor.id;
    }

    /**
     * Trata a alteração do campo patologia
     * @param {*} evt Objeto contendo os dados do evento
     * @param {Object} novaEmpresa Dados da patologia selecionada
     */
    async handlePatologiaChange(evt, novaPatologia) {
        //Altera somente se o usuário possuir permissão
        if (this.state.permiteCadastrarCliente) {
            //Cria uma cópia do estado atual e atualiza o campo
            const state = deepCopy(this.state);
            state.valores.patologia = novaPatologia;
            //Atualiza o estado da página
            this.setState(state);
        }
    }

    /**
     * Retorna os dados da patologia para exibição no campo
     * @param {Object} patologia Dados da patologia
     * @param {String} patologia.descricao Descrição da patologia
     * @returns {String} Retorna a descrição da patologia
     */
    getPatologiaLabel(patologia) {
        return patologia.descricao;
    }

    /**
     * Verifica se o item do campo está selecionado
     * @param {Object} opcao Opção exibida no campo
     * @param {Number} opcao.id Identificação da patologia
     * @param {Object} valor Opção selecionada
     * @param {Number} valor.id Identificação da patologia selecionada
     * @returns {Boolean} Retorna verdadeiro caso a opção esteja selecionada
     */
    getPatologiaSelected(opcao, valor) {
        //Compara a identificação das duas empresas
        return opcao.id === valor.id;
    }

    /**
     * Trata as alterações nos campos do formulário e atualiza o state
     * @param {*} event Objeto contendo os dados do evento
     */
    handleChange(event) {
        //Altera somente se o usuário possuir permissão
        if (this.state.permiteCadastrarCliente) {
            //Cria uma cópia do estado atual
            const state = deepCopy(this.state);
            //Atualiza somente o valor do campo alterado
            state.valores[event.target.name] = event.target.value;
            //Atualiza o estado
            this.setState(state);
        }
    }

    /**
     * Trata as alterações nos checkbox
     * @param {*} event Objeto contendo os dados do vento
     */
    handleCheckboxChange(event) {
        //Cria uma cópia do estado atual
        const state = deepCopy(this.state);
        //Atualiza somente o valor do campo alterado
        state.valores[event.target.name] = event.target.checked;
        //Atualiza o estado
        this.setState(state);
    }

    /**
     * Executa quando o form é submetido.
     * Envia os dados para a API
     * @param {*} event Objeto contendo os dados do evento
     */
    async handleSubmit(event) {
        //Impede a propagação do evento
        event.preventDefault();
        //Somente executa se o usuário tiver permissão
        if (this.state.permiteCadastrarCliente) {
            //Exibe loader
            this.setState({ processando: true });
            let result;
            //Cria uma cópia do estado atual
            const state = Object.assign({}, this.state);
            state.processando = false; //Indica que o processamento terminou
            //Salva o registro
            const id = this.getId();
            //Preenche os dados da requisição
            let dados = {
                nome: state.valores.nome,
                cpf: state.valores.cpf,
                genero: state.valores.genero,
                profissao: state.valores.profissao,
                dataNascimento: state.valores.dataNascimento,
                email: state.valores.email,
                telefone: state.valores.telefone,
                senha: state.valores.senha,
                empresa: state.valores.empresa ? state.valores.empresa.id : null,
                patologia: state.valores.patologia ? state.valores.patologia.id : null,
                ativo: state.valores.ativo,
                cep: state.valores.cep,
                logradouro: state.valores.logradouro,
                numero: state.valores.numero,
                complemento: state.valores.complemento,
                bairro: state.valores.bairro,
                municipio: state.valores.municipio,
                uf: state.valores.uf,
            };

            if (id === 0)
                result = await this.clienteController.salvar(dados);
            else
                result = await this.clienteController.atualizar({ id, ...dados });

            state.sucesso = result.sucesso;
            if (!result.sucesso) {
                state.erros = this.extrairMensagemErro(result);
            }
            this.setState(state);
        }
    }

    /**
     * Retorna as mensagens de erro da API para exibição no formulário
     * @param {*} resultado Retorno do método salvar da API
     */
    extrairMensagemErro(resultado) {
        const erros = {
            nome: '',
            cpf: '',
            genero: '',
            dataNascimento: '',
            email: '',
            telefone: '',
            senha: '',
            empresa: '',
            profissao: '',
            patologia: '',
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            municipio: '',
            uf: ''
        };

        for (let erro of resultado.erros) {
            if (erro.campo === 'nome')
                erros.nome = erro.mensagem;
            if (erro.campo === 'cpf')
                erros.cpf = erro.mensagem;
            if (erro.campo === 'genero')
                erros.genero = erro.mensagem;
            if (erro.campo === 'dataNascimento')
                erros.dataNascimento = erro.mensagem;
            if (erro.campo === 'email')
                erros.email = erro.mensagem;
            if (erro.campo === 'telefone')
                erros.telefone = erro.mensagem;
            if (erro.campo === 'senha')
                erros.senha = erro.mensagem;
            if (erro.campo === 'empresa')
                erros.empresa = erro.mensagem;
            if (erro.campo === 'patologia')
                erros.patologia = erro.mensagem;
            if (erro.campo === 'ativo')
                erros.ativo = erro.mensagem;
            if (erro.campo === 'profissao')
                erros.profissao = erro.mensagem;
            if (erro.campo === 'cep')
                erros.cep = erro.mensagem;
            if (erro.campo === 'empresa')
                erros.empresa = erro.mensagem;
            if (erro.campo === 'logradouro')
                erros.logradouro = erro.mensagem;
            if (erro.campo === 'numero')
                erros.numero = erro.mensagem;
            if (erro.campo === 'complemento')
                erros.complemento = erro.mensagem;
            if (erro.campo === 'bairro')
                erros.bairro = erro.mensagem;
            if (erro.campo === 'municipio')
                erros.municipio = erro.mensagem;
            if (erro.campo === 'uf')
                erros.uf = erro.mensagem;
        }

        return erros;
    }

    /**
     * Mapeia a lista de gêneros para itens de seleção
     * @param {Array} generos Gêneros retornados pela API
     */
    renderGeneros(generos) {
        return generos.map(genero => (
            <option key={genero.id} value={genero.id}>{genero.descricao}</option>
        ));
    }

    /**
     * Mapeia a lista de empresas para itens de seleção
     * @param {Array} empresas Empresas retornados pela API
     * @returns {Array<React.Component>} Retorna uma lista de componentes React
     */
    renderEmpresas(empresas) {
        return empresas.map(empresa => (
            <option key={empresa.id} value={empresa.id}>{empresa.cpfCnpj} - {empresa.nome}</option>
        ));
    }

    /**
     * Renderiza o campo de seleção de empresa, caso o usuário possua permissão 
     * @param {Boolean} permiteSelecionarEmpresa Indica se o usuário pode selecionar uma empresa
     * @param {String} empresa Valor selecionado
     * @param {String} empresaErro Mensagem de erro do campo
     * @param {Array} empresas Lista de empresas retornadas da API
     * @returns {React.Component} Retorna o componente ou null
     */
    renderCampoEmpresas(permiteSelecionarEmpresa, empresa, empresaErro, empresas) {
        if (permiteSelecionarEmpresa)
            return (
                <Grid item xs={12} sm={6} >
                    <Autocomplete
                        id="select-empresa"
                        options={empresas}
                        value={empresa}
                        getOptionLabel={this.getEmpresaLabel}
                        getOptionSelected={this.getEmpresaSelected}
                        onChange={this.handleEmpresaChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="empresa"
                                name="empresa"
                                label="Empresa"
                                required
                                variant="outlined"
                                error={!!empresaErro}
                                helperText={empresaErro}
                                fullWidth
                            />
                        )}
                    />
                </Grid>
            );
        else
            return null;
    }

    /**
     * Renderiza o botão salvar, caso o usuário possua permissão para o cadastro
     * @param {Boolean} permiteCadastrarCliente Permissão do usuário para cadastrar o cliente
     * @param {Boolean} processando Indica se o componente está processando
     * @returns {React.Component} Retorna o componente ou null
     */
    renderBotaoSalvar(permiteCadastrarCliente, processando) {
        if (permiteCadastrarCliente)
            return (
                <VitalizeFab color="primary" variant="extended" type="submit" loading={processando} >
                    Salvar
                </VitalizeFab>
            );
        else
            return null;
    }

    render() {
        const { onLogout, usuario, empresa: empresaLogin, permissoes } = this.props;
        const { sucesso, generos, empresas, patologias, permiteSelecionarEmpresa, permiteCadastrarCliente, processando, carregando } = this.state;
        const {
            nome,
            cpf,
            genero,
            dataNascimento,
            email,
            telefone,
            senha,
            empresa,
            patologia,
            ativo,
            profissao,
            cep,
            logradouro,
            numero,
            complemento,
            bairro,
            municipio,
            uf
        } = this.state.valores;

        const {
            nome: nomeErro,
            cpf: cpfErro,
            genero: generoErro,
            dataNascimento: dataNascimentoErro,
            email: emailErro,
            telefone: telefoneErro,
            senha: senhaErro,
            empresa: empresaErro,
            patologia: patologiaErro,
            ativo: ativoErro,
            profissao: profissaoErro,
            cep: cepErro,
            logradouro: logradouroErro,
            numero: numeroErro,
            complemento: complementoErro,
            bairro: bairroErro,
            municipio: municipioErro,
            uf: ufErro,
        } = this.state.erros;

        let senhaHelper = senhaErro;
        if (this.getId() > 0 && senhaHelper === '')
            senhaHelper = 'Deixe vazio para manter a senha';

        if (sucesso) return (<Redirect to="/cliente" />);

        return (
            <Layout onLogout={onLogout} returnLink="/cliente" usuario={usuario} empresa={empresaLogin} permissoes={permissoes} carregando={carregando} >
                <CssBaseline />
                <form onSubmit={this.handleSubmit} noValidate >
                    <VitalizePaper>
                        <Grid container spacing={1}>
                            <Grid item xs={12} >
                                <LabelSecao label="Formando" />
                            </Grid>
                            {
                                this.renderCampoEmpresas(permiteSelecionarEmpresa, empresa, empresaErro, empresas)
                            }
                            <Grid item xs={12} sm={6} >
                                <TextField
                                    name="nome"
                                    label="Nome"
                                    type="text"
                                    placeholder="Nome do formando"
                                    required
                                    variant="outlined"
                                    fullWidth
                                    error={!!nomeErro}
                                    helperText={nomeErro}
                                    value={nome}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <TextField
                                    name="cpf"
                                    label="CPF"
                                    type="text"
                                    placeholder="CPF do formando"
                                    required
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{ inputComponent: MaskedInput, inputProps: { mask: [/\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '-', /\d/, /\d/] } }}
                                    error={!!cpfErro}
                                    helperText={cpfErro}
                                    value={cpf}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <FormControl fullWidth variant="outlined" error={!!generoErro} >
                                    <InputLabel htmlFor="genero">Gênero</InputLabel>
                                    <Select
                                        id="genero"
                                        name="genero"
                                        label="Gênero"
                                        native
                                        required
                                        variant="outlined"
                                        fullWidth
                                        value={genero}
                                        onChange={this.handleChange}
                                    >
                                        <option></option>
                                        {this.renderGeneros(generos)}
                                    </Select>
                                    <FormHelperText id="genero-helper">{generoErro}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <TextField
                                    name="dataNascimento"
                                    label="Data de nascimento"
                                    type="date"
                                    placeholder="Data de nascimento do formando"
                                    required
                                    variant="outlined"
                                    fullWidth
                                    error={!!dataNascimentoErro}
                                    helperText={dataNascimentoErro}
                                    value={dataNascimento}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <TextField
                                    name="profissao"
                                    label="Profissão"
                                    type="text"
                                    placeholder="Profissão/ocupação do formando"
                                    required={false}
                                    variant="outlined"
                                    fullWidth
                                    error={!!profissaoErro}
                                    helperText={profissaoErro}
                                    value={profissao}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <Autocomplete
                                    id="select-patologia"
                                    options={patologias}
                                    value={patologia}
                                    getOptionLabel={this.getPatologiaLabel}
                                    getOptionSelected={this.getPatologiaSelected}
                                    onChange={this.handlePatologiaChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            id="patologia"
                                            name="patologia"
                                            label="Patologia principal"
                                            variant="outlined"
                                            error={!!patologiaErro}
                                            helperText={patologiaErro}
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <TextField
                                    name="email"
                                    label="E-mail"
                                    type="email"
                                    placeholder="E-mail do formando"
                                    required={false}
                                    variant="outlined"
                                    fullWidth
                                    error={!!emailErro}
                                    helperText={emailErro}
                                    value={email}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <TextField
                                    name="telefone"
                                    label="Telefone"
                                    type="phone"
                                    placeholder="Telefone do formando"
                                    required={false}
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{ inputComponent: MaskedInput, inputProps: { mask: ['(', /\d/, /\d/, ')', ' ', /\d/, ' ', /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/] } }}
                                    error={!!telefoneErro}
                                    helperText={telefoneErro}
                                    value={telefone}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} >
                                <TextField
                                    name="senha"
                                    label="Senha"
                                    type="password"
                                    placeholder="Senha do formando"
                                    required={this.getId() === 0}
                                    variant="outlined"
                                    fullWidth
                                    error={!!senhaErro}
                                    helperText={senhaHelper}
                                    value={senha}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="standard" error={!!ativoErro} >
                                    <FormControlLabel
                                        id="ativo-label"
                                        control={
                                            <Checkbox
                                                checked={ativo}
                                                onChange={this.handleCheckboxChange}
                                                name="ativo"
                                                color="primary"
                                            />
                                        }
                                        label="Ativo"
                                    />
                                    <FormHelperText id="ativo-helper" >{ativoErro}</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </VitalizePaper>
                    <VitalizePaper>
                        <Grid container spacing={1}>
                            <Grid item xs={12} >
                                <LabelSecao label="Endereço" />
                            </Grid>
                            <Grid item xs={12} sm={3} >
                                <TextField
                                    name="cep"
                                    label="CEP"
                                    type="text"
                                    placeholder="CEP"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{ inputComponent: MaskedInput, inputProps: { mask: [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/] } }}
                                    error={!!cepErro}
                                    helperText={cepErro}
                                    value={cep}
                                    onChange={this.handleCEPChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={9} >
                                <TextField
                                    name="logradouro"
                                    label="Logradouro"
                                    type="text"
                                    placeholder="Rua, Avenida..."
                                    variant="outlined"
                                    fullWidth
                                    error={!!logradouroErro}
                                    helperText={logradouroErro}
                                    value={logradouro}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} >
                                <TextField
                                    name="numero"
                                    label="Número"
                                    type="text"
                                    placeholder="Número"
                                    variant="outlined"
                                    fullWidth
                                    error={!!numeroErro}
                                    helperText={numeroErro}
                                    value={numero}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={9} >
                                <TextField
                                    name="complemento"
                                    label="Complemento"
                                    type="text"
                                    placeholder="Complemento"
                                    variant="outlined"
                                    fullWidth
                                    error={!!complementoErro}
                                    helperText={complementoErro}
                                    value={complemento}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5} >
                                <TextField
                                    name="bairro"
                                    label="Bairro"
                                    type="text"
                                    placeholder="Bairro"
                                    variant="outlined"
                                    fullWidth
                                    error={!!bairroErro}
                                    helperText={bairroErro}
                                    value={bairro}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={5} >
                                <TextField
                                    name="municipio"
                                    label="Município"
                                    type="text"
                                    placeholder="Cidade, Distrito..."
                                    variant="outlined"
                                    fullWidth
                                    error={!!municipioErro}
                                    helperText={municipioErro}
                                    value={municipio}
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2} >
                                <Autocomplete
                                    id="select-uf"
                                    options={['RO', 'AC', 'AM', 'RR', 'PA', 'AP', 'TO', 'MA', 'PI', 'CE', 'RN', 'PB', 'PE', 'AL', 'SE', 'BA', 'MG', 'ES', 'RJ', 'SP', 'PR', 'SC', 'RS', 'MS', 'MT', 'GO', 'DF']}
                                    value={uf}
                                    onChange={this.handleUFChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            id="uf"
                                            name="uf"
                                            label="UF"
                                            variant="outlined"
                                            error={!!ufErro}
                                            helperText={ufErro}
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </VitalizePaper>
                    {this.renderBotaoSalvar(permiteCadastrarCliente, processando)}
                </form>
            </Layout>
        );
    }
}

ClienteFormPage.propTypes = {
    /** Evento disparado quando o usuário realiza o logout */
    onLogout: PropTypes.func,
    /** Dados do usuário autenticado */
    usuario: PropTypes.object.isRequired,
    /** Empresa relacionada ao usuário */
    empresa: PropTypes.object,
    /** Objeto contendo as permissões do usuário */
    permissoes: PropTypes.object.isRequired,
    /** Parâmetros da URL */
    match: PropTypes.shape({
        params: PropTypes.shape({
            /** Id do cliente a ser editado */
            id: PropTypes.string
        })
    })
};

export default ClienteFormPage;